/**
 * Phase 134 — Super Admin Email Console
 * --------------------------------------------------------------------
 * Lists every lifecycle email sent through the StreamX system. KPI strip on
 * top, filters in the middle, paginated table below.
 *
 * Endpoints:
 *   GET  /api/livex/admin/emails?type=&status=&academy_id=&q=&limit=&skip=
 *   GET  /api/livex/admin/emails/metrics
 *   POST /api/livex/admin/emails/dispatch-weekly-now
 *   POST /api/livex/admin/emails/dispatch-renewals-now
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import {
  ArrowLeft, Mail, AlertTriangle, Search, RefreshCw, Send, Crown,
  TrendingUp, MailOpen, MousePointerClick, AlertCircle,
} from 'lucide-react';
import { LivexShimmerKpi, LivexShimmerRow } from '@/components/livex/LivexShimmer';
import LivexEmptyState from '@/components/livex/LivexEmptyState';
import '@/styles/livex.css';

const TYPE_LABELS = {
  ceo_welcome: 'CEO Welcome',
  livex_first_stream_celebration: 'First Stream 🎬',
  livex_pilot_limit_reached: 'Pilot Cap Reached',
  livex_upgrade_nudge: 'Upgrade Nudge',
  livex_weekly_summary: 'Weekly Digest',
  livex_renewal_reminder: 'Renewal Reminder',
  livex_activation_nudge: 'Activation Nudge 🎥',
};

const clampPct = (n) => {
  const num = Number(n ?? 0);
  if (!isFinite(num)) return 0;
  return Math.max(0, Math.min(100, num));
};

function StatusPill({ status, opened, clicked, bounced }) {
  let label = status;
  let bg = '#0c1a2e';
  let fg = '#94A3B8';
  let border = '#22314F';
  if (status === 'sent') {
    if (clicked) { label = 'clicked'; bg = '#052e2a'; fg = '#6ee7b7'; border = '#10b981'; }
    else if (opened) { label = 'opened'; bg = '#1a200a'; fg = '#facc15'; border = '#eab308'; }
    else { label = 'sent'; bg = '#0c1a2e'; fg = '#fed7aa'; border = '#FF5A1F'; }
  } else if (status === 'delivered') { bg = '#0c1a2e'; fg = '#cbd5e1'; border = '#475569'; }
  else if (status === 'failed') { bg = '#2a0a0e'; fg = '#fda4af'; border = '#dc2626'; }
  else if (status === 'bounced' || bounced) { bg = '#2a0a0e'; fg = '#fda4af'; border = '#dc2626'; label = 'bounced'; }
  else if (status === 'skipped') { bg = '#0c1a2e'; fg = '#94A3B8'; border = '#475569'; }
  else if (status === 'complained') { bg = '#2a0a0e'; fg = '#fda4af'; border = '#dc2626'; }
  return (
    <span
      data-testid={`livex-admin-email-status-${label}`}
      className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-black uppercase tracking-[0.08em] rounded"
      style={{ background: bg, color: fg, border: `1.5px solid ${border}` }}
    >
      {label}
    </span>
  );
}

function fmtDate(iso) {
  try { return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
}

export default function LivexAdminEmails() {
  const [metrics, setMetrics] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [type, setType] = useState('');
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState({ weekly: false, renewals: false, activation: false });
  const [toast, setToast] = useState('');

  const load = async () => {
    setRefreshing(true);
    setError('');
    try {
      const params = new URLSearchParams();
      params.set('limit', '100');
      if (type) params.set('type', type);
      if (status) params.set('status', status);
      if (q) params.set('q', q);
      const [listRes, metricsRes] = await Promise.all([
        api.get(`/livex/admin/emails?${params.toString()}`),
        api.get('/livex/admin/emails/metrics'),
      ]);
      setEntries(listRes.data?.entries || []);
      setMetrics(metricsRes.data || null);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load email console');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const onFilter = (e) => { e.preventDefault(); load(); };

  const triggerDispatch = async (which) => {
    setBusy((b) => ({ ...b, [which]: true }));
    setToast(''); // Clear any stale toast from a previous click.
    const labels = {
      weekly:    { endpoint: '/livex/admin/emails/dispatch-weekly-now',             noun: 'weekly digest' },
      renewals:  { endpoint: '/livex/admin/emails/dispatch-renewals-now',           noun: 'renewal reminder' },
      activation:{ endpoint: '/livex/admin/emails/dispatch-activation-nudges-now',  noun: 'activation nudge' },
    };
    const cfg = labels[which] || labels.weekly;
    try {
      const r = await api.post(cfg.endpoint, {});
      const n = r.data?.dispatched ?? 0;
      setToast(`Dispatched ${n} ${cfg.noun} email${n === 1 ? '' : 's'}`);
      await load();
    } catch (e) {
      setToast(e.response?.data?.detail || `Failed to dispatch ${cfg.noun}`);
    } finally {
      setBusy((b) => ({ ...b, [which]: false }));
      setTimeout(() => setToast(''), 6000);
    }
  };

  const Kpis = useMemo(() => {
    if (!metrics) return null;
    const k = [
      { label: 'Sent', value: metrics.totals?.sent ?? 0, icon: Mail, accent: '#FF5A1F' },
      { label: 'Open Rate', value: `${clampPct(metrics.rates?.open)}%`, icon: MailOpen, accent: '#10b981' },
      { label: 'Click Rate', value: `${clampPct(metrics.rates?.click)}%`, icon: MousePointerClick, accent: '#3b82f6' },
      { label: 'Bounce Rate', value: `${clampPct(metrics.rates?.bounce)}%`, icon: AlertCircle, accent: '#dc2626' },
    ];
    return (
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="livex-admin-email-kpis">
        {k.map((tile) => (
          <div key={tile.label} className="p-4 bg-[#07111F] border-2 border-[#22314F] rounded-xl">
            <tile.icon className="h-4 w-4 mb-2" style={{ color: tile.accent }} />
            <div className="text-2xl sm:text-3xl font-black text-white tabular-nums leading-none">{tile.value}</div>
            <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#94A3B8] mt-1.5">{tile.label}</div>
          </div>
        ))}
      </div>
    );
  }, [metrics]);

  return (
    <div className="min-h-screen text-white" style={{ background: '#0a0a0f' }} data-testid="livex-admin-emails-page">
      {/* Top bar */}
      <div className="border-b-2 border-[#22314F] bg-[#07111F]/95 backdrop-blur sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/admin/livex" data-testid="livex-admin-emails-back" className="flex-shrink-0 hover:opacity-80 transition-opacity">
              <ArrowLeft className="h-5 w-5 text-white" />
            </Link>
            <div className="h-10 w-10 rounded-xl grid place-items-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #dc2626, #f97316)' }}>
              <Mail className="h-5 w-5 text-white" />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] font-black tracking-[0.25em] text-[#FF5A1F] uppercase">SUPER ADMIN</div>
              <h1 className="text-lg sm:text-xl font-black text-white tracking-tight">Email Console</h1>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => triggerDispatch('weekly')}
              disabled={busy.weekly}
              data-testid="livex-admin-emails-dispatch-weekly"
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-white bg-[#0c1a2e] border-2 border-[#22314F] hover:border-[#FF5A1F] rounded-lg transition-all disabled:opacity-50"
            >
              <Send className="h-3 w-3" /> {busy.weekly ? 'Dispatching…' : 'Run Weekly Digest'}
            </button>
            <button
              onClick={() => triggerDispatch('renewals')}
              disabled={busy.renewals}
              data-testid="livex-admin-emails-dispatch-renewals"
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-white bg-[#0c1a2e] border-2 border-[#22314F] hover:border-[#FF5A1F] rounded-lg transition-all disabled:opacity-50"
            >
              <Send className="h-3 w-3" /> {busy.renewals ? 'Dispatching…' : 'Run Renewal Reminders'}
            </button>
            <button
              onClick={() => triggerDispatch('activation')}
              disabled={busy.activation}
              data-testid="livex-admin-emails-dispatch-activation"
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-white bg-[#0c1a2e] border-2 border-[#22314F] hover:border-[#FF5A1F] rounded-lg transition-all disabled:opacity-50"
            >
              <Send className="h-3 w-3" /> {busy.activation ? 'Dispatching…' : 'Run Activation Nudges'}
            </button>
            <button
              onClick={load}
              disabled={refreshing}
              data-testid="livex-admin-emails-refresh"
              aria-label="Refresh email console"
              className="inline-flex items-center justify-center w-9 h-9 text-white bg-[#0c1a2e] border-2 border-[#22314F] hover:border-[#FF5A1F] rounded-lg transition-all disabled:opacity-50"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        {toast && (
          <div className="p-3 bg-emerald-950 border-2 border-emerald-700 rounded-xl text-emerald-100 text-sm font-bold" data-testid="livex-admin-emails-toast">{toast}</div>
        )}

        {/* KPI strip */}
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {Array.from({ length: 4 }).map((_, i) => <LivexShimmerKpi key={i} />)}
          </div>
        ) : Kpis}

        {/* Insight strip — most effective + upgrade conversion */}
        {metrics && (
          <div className="grid sm:grid-cols-2 gap-3" data-testid="livex-admin-emails-insights">
            <div className="p-4 bg-[#07111F] border-2 border-[#22314F] rounded-xl">
              <TrendingUp className="h-4 w-4 text-emerald-400 mb-1.5" />
              <div className="text-[10px] font-black uppercase tracking-[0.15em] text-[#94A3B8] mb-1">Most Effective Email</div>
              <div className="text-base font-black text-white">
                {metrics.most_effective?.type
                  ? `${TYPE_LABELS[metrics.most_effective.type] || metrics.most_effective.type} — ${clampPct(metrics.most_effective.open_rate)}% open`
                  : 'Need 5+ sends per type to rank'}
              </div>
            </div>
            <div className="p-4 bg-[#07111F] border-2 border-[#22314F] rounded-xl">
              <Crown className="h-4 w-4 text-amber-400 mb-1.5" />
              <div className="text-[10px] font-black uppercase tracking-[0.15em] text-[#94A3B8] mb-1">Upgrade Conversion</div>
              <div className="text-base font-black text-white">
                {metrics.upgrade_conversion?.converted ?? 0} of {metrics.upgrade_conversion?.nudges_sent ?? 0} nudged academies are on Pro
                {(metrics.upgrade_conversion?.nudges_sent ?? 0) > 0 && (
                  <span className="text-[#FF5A1F]"> · {clampPct(metrics.upgrade_conversion?.rate)}%</span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <form onSubmit={onFilter} className="grid grid-cols-1 sm:grid-cols-[1fr_180px_180px_auto] gap-2" data-testid="livex-admin-emails-filters">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
            <input
              data-testid="livex-admin-emails-filter-q"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search recipient or subject"
              className="w-full pl-10 pr-3 py-2.5 text-sm font-bold text-white bg-[#07111F] border-2 border-[#22314F] focus:border-[#FF5A1F] outline-none rounded-lg"
            />
          </div>
          <select
            data-testid="livex-admin-emails-filter-type"
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="px-3 py-2.5 text-sm font-bold text-white bg-[#07111F] border-2 border-[#22314F] focus:border-[#FF5A1F] outline-none rounded-lg"
          >
            <option value="">All types</option>
            {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select
            data-testid="livex-admin-emails-filter-status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="px-3 py-2.5 text-sm font-bold text-white bg-[#07111F] border-2 border-[#22314F] focus:border-[#FF5A1F] outline-none rounded-lg"
          >
            <option value="">All statuses</option>
            <option value="sent">Sent</option>
            <option value="delivered">Delivered</option>
            <option value="failed">Failed</option>
            <option value="bounced">Bounced</option>
            <option value="skipped">Skipped</option>
            <option value="complained">Complained</option>
          </select>
          <button
            type="submit"
            data-testid="livex-admin-emails-apply-filters"
            className="px-4 py-2.5 text-sm font-bold text-white bg-[#0c1a2e] border-2 border-[#22314F] hover:border-[#FF5A1F] rounded-lg transition-all"
          >Apply</button>
        </form>

        {/* Error */}
        {error && (
          <div className="p-4 bg-rose-950 border-2 border-rose-700 rounded-xl text-rose-100 text-sm font-bold flex items-start gap-2" data-testid="livex-admin-emails-error">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" /> {error}
          </div>
        )}

        {/* List */}
        {loading ? (
          <div className="space-y-2" data-testid="livex-admin-emails-loading">
            {Array.from({ length: 5 }).map((_, i) => <LivexShimmerRow key={i} />)}
          </div>
        ) : entries.length === 0 ? (
          <LivexEmptyState
            testid="livex-admin-emails-empty"
            icon={Mail}
            title={q || type || status ? 'No emails match this filter' : 'No emails sent yet'}
            body={q || type || status
              ? 'Clear the search or pick different filters to widen the view.'
              : 'Once academies sign up or stream, lifecycle emails will start showing up here.'}
            compact
          />
        ) : (
          <div className="space-y-2">
            {entries.map((row) => (
              <div
                key={row.id}
                data-testid={`livex-admin-email-${row.id}`}
                className="p-4 bg-[#07111F] border-2 border-[#22314F] rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-3 livex-card-lift"
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <Mail className="h-4 w-4 text-[#FF5A1F] flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] font-black uppercase tracking-[0.1em] px-1.5 py-0.5 bg-[#0c1a2e] border border-[#22314F] text-white rounded">
                        {TYPE_LABELS[row.type] || row.type}
                      </span>
                      <StatusPill
                        status={row.status}
                        opened={!!row.opened_at}
                        clicked={!!row.clicked_at}
                        bounced={!!row.bounced_at}
                      />
                    </div>
                    <div className="text-sm font-bold text-white truncate mt-1">{row.subject}</div>
                    <div className="text-[11px] font-bold text-[#94A3B8] truncate">
                      To: <span className="text-white">{row.to}</span>
                      {row.academy_id && <span> · acad: <span className="font-mono">{row.academy_id.slice(0, 8)}</span></span>}
                    </div>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-[11px] font-bold text-[#94A3B8] tabular-nums">{fmtDate(row.created_at)}</div>
                  {row.error && (
                    <div className="text-[10px] font-bold text-rose-300 mt-0.5 max-w-[220px] truncate" title={row.error}>{row.error}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
